<script setup lang="ts">
const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { field } = useInlineBlockEditor(props.id);
const route = useRoute();
const injectedReportUserId = inject<{ value?: string } | null>("reportUserId", null);

const containerId = field<string>("containerId", "");
const containerPath = field<string>("containerPath", "/");
const path = field<string>("path", "voice-notes");
const filePrefix = field<string>("filePrefix", "voice-note");
const maxDurationSeconds = field<number>("maxDurationSeconds", 300);
const showExistingList = field<boolean>("showExistingList", true);
const mkdirOnUpload = field<boolean>("mkdirOnUpload", true);
const editorCandidateId = computed(() =>
  injectedReportUserId?.value
  ?? (typeof route.query.user === "string" ? route.query.user : null),
);
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:mic" accent="media">
    <EditorField :label="t('containerIdLabel')">
      <Input
        v-model="containerId"
        class="h-8 rounded-sm bg-muted text-[13px]"
        placeholder="my-container"
      />
    </EditorField>

    <EditorField :label="t('containerPathLabel')" :hint="t('templateHint')">
      <EditorPathInput
        v-model="containerPath"
        mode="existing-folder"
        :scopes="['user']"
        default-scope="user"
        :user-id="editorCandidateId"
        placeholder="/data/<userId>"
        aria-label="Voice note container path"
      />
    </EditorField>

    <EditorField :label="t('pathLabel')">
      <EditorPathInput
        v-model="path"
        mode="existing-folder"
        :scopes="['site']"
        placeholder="voice-notes"
        aria-label="Voice note sub-folder"
      />
    </EditorField>

    <EditorField :label="t('filePrefixLabel')">
      <Input
        v-model="filePrefix"
        class="h-8 rounded-sm bg-muted text-[13px]"
        placeholder="voice-note"
      />
    </EditorField>

    <EditorField :label="t('maxDurationLabel')" :hint="t('maxDurationHint')">
      <Input
        v-model.number="maxDurationSeconds"
        class="h-8 rounded-sm bg-muted text-[13px]"
        type="number"
        min="5"
        step="5"
      />
    </EditorField>

    <EditorField :label="t('showExistingListLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("showExistingListHint") }}</span>
        <Switch v-model="showExistingList" />
      </div>
    </EditorField>

    <EditorField :label="t('mkdirOnUploadLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("mkdirOnUploadHint") }}</span>
        <Switch v-model="mkdirOnUpload" />
      </div>
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Voice Note"
  containerIdLabel: "Container ID"
  containerPathLabel: "Container Path"
  pathLabel: "Sub-folder"
  filePrefixLabel: "Filename Prefix"
  maxDurationLabel: "Max Recording Duration (seconds)"
  maxDurationHint: "Recording stops automatically after this many seconds."
  templateHint: "Candidate-scoped storage. Existing template-based paths remain supported."
  showExistingListLabel: "Show existing voice notes"
  showExistingListHint: "Display saved recordings below the recorder."
  mkdirOnUploadLabel: "Create missing folders"
  mkdirOnUploadHint: "Create parent folders during upload."
</i18n>
